import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useState, useRef, useEffect, useCallback } from 'react';
import jsQR from 'jsqr';
import { C } from '@parsina/framework/clinical/src/constants/theme';

/**
 * Prefer the native detector, but only where it actually decodes.
 *
 * Chrome exposes BarcodeDetector on platforms whose backing service is missing —
 * on Android it is a Play Services module fetched on demand — and there detect()
 * resolves to an empty array instead of throwing. So probe it once, and never
 * trust it as the only decoder.
 */
async function makeDetector() {
  try {
    if (!('BarcodeDetector' in window)) return null;
    const formats = await window.BarcodeDetector.getSupportedFormats();
    if (!formats.includes('qr_code')) return null;
    return new window.BarcodeDetector({ formats: ['qr_code'] });
  } catch {
    return null;
  }
}

/**
 * Camera QR scanner. Calls onDetected(rawValue) once, then stops the stream.
 */
export default function QrScanner({ onDetected, onClose, height = 240 }) {
  const { t } = useTranslation();
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const rafRef    = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState('');

  // Held in a ref so a fresh inline callback from the parent can't retrigger the
  // effect and tear the camera down mid-scan.
  const onDetectedRef = useRef(onDetected);
  useEffect(() => { onDetectedRef.current = onDetected; }, [onDetected]);

  const stopScan = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(text => text.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    // Decode from a canvas frame. Works in every browser with a camera. Kept at
    // capture resolution — shrinking the frame shrinks the QR's modules and is
    // the quickest way to make a code stop being readable.
    const MAX_EDGE = 1280;
    function decodeWithJsQr(video) {
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (!vw || !vh) return null; // metadata not in yet
      const scale = Math.min(1, MAX_EDGE / Math.max(vw, vh));
      const w = Math.round(vw * scale);
      const h = Math.round(vh * scale);
      const canvas = (canvasRef.current ||= document.createElement('canvas'));
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(video, 0, 0, w, h);
      const { data } = ctx.getImageData(0, 0, w, h);
      const result = jsQR(data, w, h, { inversionAttempts: 'attemptBoth' });
      return result?.data || null;
    }

    async function start() {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          // A sharp stream matters: at the default resolution a QR's modules can
          // be too few pixels wide to resolve. `ideal` degrades gracefully.
          video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        });
      } catch {
        if (mounted) setError(t('errors.camera.unavailable'));
        return;
      }
      if (!mounted) { stream.getTracks().forEach(text => text.stop()); return; }

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try { await videoRef.current.play(); } catch { /* autoplay race — frames still arrive */ }
      }

      let detector = await makeDetector();
      const JSQR_INTERVAL_MS = 120;
      let lastJsQrAt = 0;

      const tick = async () => {
        if (!mounted || !videoRef.current) return;
        let value = null;

        // Native first when present — it's the cheap path. But it can be present
        // and still never decode, so jsQR gets every frame it comes back empty
        // on. Only a throw retires the native detector for good.
        if (detector) {
          try {
            const codes = await detector.detect(videoRef.current);
            if (codes.length > 0) value = codes[0].rawValue;
          } catch {
            detector = null;
          }
        }
        const now = performance.now();
        if (!value && now - lastJsQrAt >= JSQR_INTERVAL_MS) {
          lastJsQrAt = now;
          try { value = decodeWithJsQr(videoRef.current); } catch { /* frame not ready */ }
        }

        if (value) {
          stopScan();
          onDetectedRef.current?.(value);
          return;
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    }

    start();
    return () => { mounted = false; stopScan(); };
  }, [stopScan]);

  if (error) {
    return (
      <div style={{
        padding: 12, borderRadius: 12, background: '#FEF2F2',
        color: '#B91C1C', fontSize: 12, 
      }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{
      position: 'relative', height, borderRadius: 12,
      overflow: 'hidden', background: '#000',
    }}>
      <video
        ref={videoRef}
        muted
        playsInline
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
      {/* Viewfinder */}
      <div style={{
        position: 'absolute', inset: 0, display: 'flex',
        alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
      }}>
        <div style={{
          width: 150, height: 150, borderRadius: 12,
          border: `2px solid ${C.teal}`,
          boxShadow: '0 0 0 2000px rgba(0,0,0,0.4)',
        }} />
      </div>
      {onClose && (
        <button
          type="button"
          onClick={() => { stopScan(); onClose(); }}
          style={{
            position: 'absolute', top: 8, left: 8, width: 30, height: 30,
            borderRadius: '50%', border: 'none', cursor: 'pointer',
            background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 16, lineHeight: 1,
          }}
        >
          ✕
        </button>
      )}
    </div>
  );
}
